import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, Users, UserCog, LogOut } from 'lucide-react';
import {
  Sidebar as SidebarUI,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/shared/ui/sidebar';
import { cn } from '@/lib/utils';
import { useAuth, type AuthState } from '@/hooks/useAuth';
import { useRoutingUserDetails } from '@/hooks/useRoutingUserDetails';
import { UserRole } from '@cargovibe/shared-types';
import type { RoutingUserDetails } from '@/lib/api/routingApi';
import type { UseQueryResult } from '@/lib/query';
import { Location } from 'react-router';

interface NavItem {
  path: string;
  labelKey: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

const baseNavItems: NavItem[] = [
  { path: '/users', labelKey: 'sidebar.users', icon: Users },
  { path: '/bookings', labelKey: 'sidebar.bookings', icon: CalendarCheck },
];

const IMPERSONATE_ROLES: ReadonlySet<UserRole> = new Set([UserRole.Admin]);

export function Sidebar(): React.JSX.Element {
  const { t } = useTranslation();
  const location: Location = useLocation();
  const { state } = useSidebar();
  const { logout }: AuthState = useAuth();
  const { data: userDetails }: UseQueryResult<RoutingUserDetails | null> = useRoutingUserDetails();
  const isCollapsed: boolean = state === 'collapsed';

  const canImpersonate: boolean = userDetails != null && IMPERSONATE_ROLES.has(userDetails.role);
  const navItems: NavItem[] = canImpersonate
    ? [
        ...baseNavItems,
        { path: '/impersonateUser', labelKey: 'sidebar.impersonate', icon: UserCog },
      ]
    : baseNavItems;

  return (
    <SidebarUI collapsible="icon">
      <SidebarHeader className="h-16 flex items-center px-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3 overflow-hidden">
          <img
            src="/images/logo-color-zoomed_192x192.png"
            alt="cargovibe"
            className="w-9 h-9 rounded-lg shrink-0 shadow-sm"
          />
          {!isCollapsed && (
            <span className="text-lg font-bold tracking-tight text-white whitespace-nowrap">
              cargovibe
            </span>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="p-2">
        <SidebarMenu>
          {navItems.map((item) => {
            const isActive: boolean = location.pathname.startsWith(item.path);

            return (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  tooltip={t(item.labelKey)}
                  className={cn(
                    'transition-all duration-200',
                    isActive
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                  )}
                >
                  <NavLink to={item.path}>
                    <item.icon size={20} />
                    <span>{t(item.labelKey)}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2 gap-1">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="text-sidebar-foreground/70 hover:bg-destructive/10 hover:text-destructive"
              tooltip={t('sidebar.logout')}
              onClick={() => void logout()}
            >
              <LogOut size={20} />
              <span>{t('sidebar.logout')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </SidebarUI>
  );
}
